import React from 'react';
import { format } from 'date-fns';

import './index.less';

import { lang } from './lang';
import { FRIENDLY } from 'ck-core/source/data/DateFormat';
import { MonetaryValue } from 'ck-core/source/classes/MonetaryValue';
import { PLAN_SIZE } from 'ck-core/source/data/PlanSize';
import { getPlanDescription, PlanType } from 'ck-core/source/data/PlanType';
import { Choices } from '../CheckoutStages';

export const WHEN_REDEEMED = Symbol('WHEN_REDEEMED');
export const DATE_FORMAT = FRIENDLY;

export interface Props {
	choices: Choices;
	size: PLAN_SIZE;
	individualFullPrice: MonetaryValue;
	familyUpgradePrice: MonetaryValue;
	totalPrice: MonetaryValue;
	upgradeDiscount?: MonetaryValue;
	coupon?: {
		name: string;
		discount: MonetaryValue;
	};

	paymentDate: Date;
	/**
	 * Symbol must be WHEN_REDEEMED
	 * See https://github.com/Microsoft/TypeScript/issues/20898
	 */
	startDate: Date | Symbol;
	endDate?: Date;
	nextPaymentDate?: Date;
}

export const PaymentSummaryTable: React.SFC<Props> = ({
	size,
	individualFullPrice,
	familyUpgradePrice,
	totalPrice,
	upgradeDiscount,
	coupon,
	choices,
	paymentDate,
	startDate,
	endDate,
	nextPaymentDate
}) => {
	const family = size === PLAN_SIZE.FAMILY;

	const fullPrice = new MonetaryValue(
		individualFullPrice.value +
			(family ? familyUpgradePrice.value : 0) -
			(upgradeDiscount ? upgradeDiscount.value : 0),
		individualFullPrice.currency
	);

	return (
		<div className="PaymentSummaryTable">
			{choices.size && choices.product && choices.duration ? (
				<div className="PaymentSummaryTable__plan">{getPlanDescription(choices as PlanType)}</div>
			) : null}
			<table className="PaymentSummaryTable__table">
				<tbody>
					<tr className="PaymentSummaryTable__row">
						<td>{lang.PRICE}</td>
						<td>{fullPrice.format()}</td>
					</tr>
					{coupon && (
						<tr className="PaymentSummaryTable__row PaymentSummaryTable__row--coupon">
							<td>{`Discount - ${coupon.name}`}</td>
							<td>{coupon.discount.format()}</td>
						</tr>
					)}
					<tr className="PaymentSummaryTable__row PaymentSummaryTable__row--total">
						<td>{lang.TOTAL}</td>
						<td>{totalPrice.format()}</td>
					</tr>
				</tbody>
			</table>
			<table className="PaymentSummaryTable__table">
				<tbody>
					<tr className="PaymentSummaryTable__row">
						<td>{lang.PAYMENT_DATE}</td>
						<td>{format(paymentDate, DATE_FORMAT)}</td>
					</tr>
					{endDate && (
						<tr className="PaymentSummaryTable__row">
							<td>{lang.END_DATE}</td>
							<td>{format(endDate, DATE_FORMAT)}</td>
						</tr>
					)}
					{nextPaymentDate && (
						<tr className="PaymentSummaryTable__row PaymentSummaryTable__row--nextPaymentDate">
							<td className="PaymentSummaryTable__row-label">{lang.NEXT_PAYMENT_DATE}</td>
							<td className="PaymentSummaryTable__row-value">
								{format(nextPaymentDate, DATE_FORMAT)}
							</td>
						</tr>
					)}
				</tbody>
			</table>
			<div className="PaymentSummaryTable__authorisation">
				I authorise {CK.config.name === 'ck-coolmath' ? 'Coolmath Coding' : 'Code Kingdoms'} to send instructions to the financial institution that issued my
				card to take payments from my card account in accordance with the terms of my agreement with
				you.
			</div>
		</div>
	);
};
